import { DotMatrix } from '@clickguard/design-system'
import React, { forwardRef } from 'react'
import { cva } from 'class-variance-authority'
import { VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * ClickGuard Button Variants — derived from clickguard.com CSS (March 2026)
 *
 * Primary:         Lemon bg #EEFF9D, border #BAE101, navy text, pill shape (.button)
 * Blue:            Solid purple #554BFD, white text (.button.is-blue)
 * Purple Gradient: #554BFD → #9A94FF (tab overlays, active states)
 * Secondary:       Transparent, navy border #111553, hover fills navy (.button.is-secondary)
 * Secondary White: Transparent, white border, for dark bgs (.button.is-secondary-white)
 * Navy:            Solid #111553, white text
 * Ghost:           Transparent, navy text, hover gray
 */
const buttonVariants = cva(
  [
    'cg-inline-flex cg-items-center cg-justify-center cg-font-medium cg-font-sans',
    'cg-transition-all cg-duration-200',
    'focus-visible:cg-outline-none focus-visible:cg-ring-2 focus-visible:cg-ring-primary-500 focus-visible:cg-ring-offset-2',
    'disabled:cg-pointer-events-none disabled:cg-opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        // Primary CTA — lemon/yellow-green (.button on clickguard.com)
        // bg: #eeff9d, border: #bae101, text: #111553, shadow: #d2fb0c66
        primary:
          'cg-bg-lemon-bg cg-text-navy-500 cg-border cg-border-lemon-dark hover:cg-bg-lemon-dark active:cg-bg-lemon-dark cg-shadow-yellow-glow',
        // Solid purple — .button.is-blue on clickguard.com
        blue: 'cg-bg-primary-500 cg-text-white cg-border cg-border-primary-500 hover:cg-bg-primary-700 active:cg-bg-primary-800',
        // Purple gradient — used in tab overlays, active states
        // linear-gradient(132deg, #554bfd, #9a94ff)
        'purple-gradient':
          'cg-bg-gradient-to-r cg-from-primary-500 cg-to-primary-300 cg-text-white hover:cg-from-primary-700 hover:cg-to-primary-400 active:cg-from-primary-800 active:cg-to-primary-500',
        // Secondary — .button.is-secondary on clickguard.com
        // transparent bg, navy border, hover fills navy + white text
        secondary:
          'cg-bg-transparent cg-text-navy-500 cg-border cg-border-navy-500 hover:cg-bg-navy-500 hover:cg-text-gray-0 active:cg-bg-navy-600 active:cg-text-white',
        // Secondary White — .button.is-secondary-white (for dark backgrounds)
        'secondary-white':
          'cg-bg-transparent cg-text-gray-0 cg-border cg-border-gray-0 hover:cg-bg-gray-0 hover:cg-text-navy-500 active:cg-bg-white active:cg-text-navy-500',
        // Navy solid — matches #111553 dark buttons
        navy: 'cg-bg-navy-500 cg-text-white hover:cg-bg-navy-600 active:cg-bg-navy-700',
        // White button — .button.is-white on clickguard.com
        white: 'cg-bg-gray-0 cg-text-navy-500 hover:cg-bg-white active:cg-bg-white',
        // Ghost — transparent bg
        ghost: 'cg-bg-transparent cg-text-navy-500 hover:cg-bg-gray-3 active:cg-bg-gray-10',
        // Border secondary — .button.is-border-secondary
        // lemon-10 bg, rounded-lg, no shadow
        'border-secondary':
          'cg-bg-lemon-light cg-text-navy-500 cg-border cg-border-lemon-dark hover:cg-bg-lemon-bg active:cg-bg-lemon-bg',
        // Danger / destructive
        danger: 'cg-bg-red-500 cg-text-white hover:cg-bg-red-600 active:cg-bg-red-700',
        // Success
        success: 'cg-bg-green-500 cg-text-white hover:cg-bg-green-600 active:cg-bg-green-700',
        // Warning
        warning: 'cg-bg-amber-500 cg-text-white hover:cg-bg-amber-600 active:cg-bg-amber-700',
        // Lavender — matches light purple bg (#eeedff)
        lavender:
          'cg-bg-lavender cg-text-primary-500 cg-font-semibold hover:cg-bg-primary-100 active:cg-bg-primary-200',
      },
      size: {
        xxs: 'cg-h-6 cg-px-2 cg-text-xs cg-rounded-full',
        xs: 'cg-h-7 cg-px-3 cg-text-xs cg-rounded-full',
        sm: 'cg-h-8 cg-px-4 cg-text-sm cg-rounded-full',
        md: 'cg-h-10 cg-px-5 cg-text-base cg-rounded-full',
        lg: 'cg-h-11 cg-px-6 cg-text-lg cg-rounded-full',
        xl: 'cg-h-12 cg-px-7 cg-text-lg cg-rounded-full',
      },
      fullWidth: {
        true: 'cg-w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Shows a loading indicator and disables the button */
  isLoading?: boolean
  /** Icon element to render before button text */
  leftIcon?: React.ReactNode
  /** Icon element to render after button text */
  rightIcon?: React.ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      isLoading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={twMerge(clsx(buttonVariants({ variant, size, fullWidth }), className))}
        disabled={disabled || isLoading}
        aria-busy={isLoading}
        {...props}
      >
        {isLoading && (
          <DotMatrix size="xs" tone="inherit" className="cg-mr-2 cg-shrink-0" />
        )}
        {!isLoading && leftIcon && <span className="cg-mr-2">{leftIcon}</span>}
        {children}
        {!isLoading && rightIcon && <span className="cg-ml-2">{rightIcon}</span>}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button, buttonVariants }
